import { useCallback, useMemo, useState } from 'react';

import type { JiraIssue } from '~/services/api/jira';

interface UseJiraGroupingReturn {
  collapsedSections: Set<JiraSection>;
  groups: JiraGroup[];
  toggleSection: (section: JiraSection) => void;
}

export type JiraSection = 'new' | 'cloned' | 'done';

export interface JiraGroup {
  key: JiraSection;
  label: string;
  issues: JiraIssue[];
}

const STORAGE_KEY = 'jira-collapsed-sections';

const DONE_STATUSES = new Set(['closed', 'done', 'resolved']);

function isDoneStatus(status: string): boolean {
  return DONE_STATUSES.has(status.toLowerCase());
}

function readCollapsedSections(): Set<JiraSection> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored) as JiraSection[]);
  } catch {
    // ignore
  }
  return new Set<JiraSection>();
}

function writeCollapsedSections(sections: Set<JiraSection>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...sections]));
  } catch {
    // ignore quota errors
  }
}

export function useJiraGrouping(issues: JiraIssue[]): UseJiraGroupingReturn {
  const [collapsedSections, setCollapsedSections] =
    useState<Set<JiraSection>>(readCollapsedSections);

  const toggleSection = useCallback((section: JiraSection) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      writeCollapsedSections(next);
      return next;
    });
  }, []);

  const groups = useMemo((): JiraGroup[] => {
    const newIssues: JiraIssue[] = [];
    const clonedIssues: JiraIssue[] = [];
    const doneIssues: JiraIssue[] = [];

    for (const issue of issues) {
      if (isDoneStatus(issue.status)) {
        doneIssues.push(issue);
      } else if (issue.linked_project_id) {
        clonedIssues.push(issue);
      } else {
        newIssues.push(issue);
      }
    }

    return [
      { key: 'new', label: 'New', issues: newIssues },
      { key: 'cloned', label: 'Cloned', issues: clonedIssues },
      { key: 'done', label: 'Done', issues: doneIssues },
    ];
  }, [issues]);

  return { collapsedSections, groups, toggleSection };
}
